import {
  FC,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Command as CommandPrimitive } from "cmdk";
import { CheckIcon, ChevronDown, Search, XIcon } from "lucide-react";

import { RmBadge } from "@/components/RmBadge";
import { RmButton } from "@/components/RmButton/RmButton";
import {
  RmCommandEmpty,
  RmCommandGroup,
  RmCommandInput,
  RmCommandItem,
  RmCommandList,
  RmCommandLoading,
} from "@/components/RmCommand";
import {
  RmPopover,
  RmPopoverContent,
  RmPopoverTrigger,
} from "@/components/RmPopover";
import { NumberOptionType, OptionType } from "@/domain/common";
import { useFuseSearch } from "@/hooks/useFuseSearch";
import { useTranslate } from "@/hooks/useTranslate";
import { cn } from "@/utils/tailwind";

import { BaseTextSelectPropsType } from "./BaseTextSelect";

type BaseMultiTextSelectPropsType = BaseTextSelectPropsType & {
  values: string[];
  onSelectionChange: (values: (string | number)[]) => void;
};

export const BaseMultiTextSelect: FC<BaseMultiTextSelectPropsType> = ({
  values,
  options,
  placeholderI18nKey,
  placeholderI18nOptions,
  emptyI18nKey,
  emptyI18nOptions,
  isLoading,
  isDisabled,
  onSelectionChange,
  onInputChange,
  isUsingAsyncData,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<
    (OptionType | NumberOptionType)[]
  >(
    values
      ?.map(value => options.find(o => o.value === value))
      .filter(Boolean) as OptionType[],
  );
  const [inputValue, setInputValue] = useState<string>("");

  // Need to use Fuse for client side filtering to fix issue with list not updating when a selection is made and the input value is reset
  const { results: filterResults, onSearch } = useFuseSearch<
    OptionType | NumberOptionType
  >({
    list: options,
    options: {
      keys: ["label"],
    },
    isEnabled: !isUsingAsyncData,
  });

  const placeholder = useTranslate(placeholderI18nKey, placeholderI18nOptions);
  const emptyMessage = useTranslate(
    emptyI18nKey ?? "common:noResultsFound",
    emptyI18nOptions,
  );

  // When input value is changed, trigger the filtering with Fuse
  useEffect(() => {
    onSearch(inputValue);
  }, [inputValue, onSearch]);

  // Handle the case when the value is changed from the outside, like form.setValue
  useEffect(() => {
    onSelectionChange(selectedOptions.map(o => o.value));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOptions]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      const input = inputRef.current;
      if (!input) {
        return;
      }

      // Keep the options displayed when the user is typing
      if (!isOpen) {
        setIsOpen(true);
      }

      // Select the option if the user presses Enter
      if (e.key === "Delete" || e.key === "Backspace") {
        if (input.value === "") {
          setSelectedOptions(prev => {
            const newSelectedOptions = [...prev];
            newSelectedOptions.pop();
            return newSelectedOptions;
          });
        }
      }
      // This is not a default behaviour of the <input /> field
      if (e.key === "Escape") {
        input.blur();
      }
    },
    [isOpen],
  );

  const handleInputChange = useCallback(
    (value: string) => {
      onInputChange?.(value);
      setInputValue(value);
    },
    [onInputChange],
  );

  const handleBlur = useCallback(() => {
    setIsOpen(false);
    if (selectedOptions.length === 0) {
      setInputValue("");
    }
  }, [selectedOptions]);

  const handleSelectOption = useCallback(
    (option: OptionType | NumberOptionType) => {
      setInputValue("");
      setSelectedOptions(prev => [...prev, option]);
    },
    [],
  );

  const handleUnselectOption = useCallback(
    (option: OptionType | NumberOptionType) => {
      setSelectedOptions(prev => prev.filter(s => s.value !== option.value));
    },
    [],
  );

  const shouldShowList = isUsingAsyncData ? Boolean(inputValue) : true;

  const filteredOptions = useMemo(() => {
    if (isUsingAsyncData || (!inputValue && !filterResults)) {
      return options;
    }
    return filterResults?.map(r => r.item) ?? [];
  }, [isUsingAsyncData, filterResults, inputValue, options]);

  return (
    <RmPopover open={isOpen}>
      <CommandPrimitive
        data-slot="command"
        shouldFilter={false}
        className="overflow-visible bg-background">
        <RmPopoverTrigger asChild>
          <div
            className={cn(
              "flex min-h-9 w-full min-w-0 flex-wrap items-center gap-1 rounded-md border border-input bg-background px-3 py-[5px] text-base shadow-xs ring-ring/50 transition-[color,box-shadow] selection:bg-primary selection:text-primary-foreground dark:bg-input/30",
              {
                "ring-3": isOpen,
                "gap-y-1": selectedOptions.length > 0,
              },
            )}
            onClick={() => !isDisabled && setIsOpen(true)}>
            {isUsingAsyncData && (
              <Search
                className={cn(
                  "mr-1.75 -ml-1.25 size-4 shrink-0 text-muted-foreground",
                  {
                    "mr-0.75": selectedOptions.length > 0,
                  },
                )}
              />
            )}
            {selectedOptions.map(option => {
              return (
                <RmBadge
                  key={option.value}
                  className="h-6 first:mr-0 last-of-type:mr-1">
                  <div className="inline-flex items-center gap-1">
                    {option.label}
                    <RmButton
                      type="button"
                      size="icon"
                      className="size-3 cursor-pointer text-muted-foreground hover:text-muted-foreground/50"
                      onKeyDown={e => {
                        if (e.key === "Enter") {
                          handleUnselectOption(option);
                        }
                      }}
                      onMouseDown={e => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={e => {
                        handleUnselectOption(option);
                        e.preventDefault();
                        e.stopPropagation();
                      }}>
                      <XIcon className="size-3 shrink-0" />
                    </RmButton>
                  </div>
                </RmBadge>
              );
            })}
            <RmCommandInput
              ref={inputRef}
              value={inputValue}
              onValueChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              onFocus={() => setIsOpen(true)}
              placeholder={selectedOptions.length > 0 ? "" : placeholder}
              disabled={isDisabled}
              className="h-5 min-w-32 rounded-none border-0 p-0 shadow-none focus-visible:ring-0 dark:bg-transparent"
              rightElement={
                isUsingAsyncData ? undefined : (
                  <ChevronDown className="-mr-6.5 size-4 shrink-0" />
                )
              }
            />
          </div>
        </RmPopoverTrigger>
        <RmPopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0"
          hidden={!shouldShowList}
          onOpenAutoFocus={e => e.preventDefault()}>
          <RmCommandList>
            {filteredOptions &&
              (isLoading ? (
                <RmCommandLoading />
              ) : (
                <RmCommandGroup>
                  {filteredOptions.map(option => (
                    <RmCommandItem
                      key={option.value}
                      onMouseDown={event => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                      onSelect={() => {
                        if (
                          selectedOptions.some(so => so.value === option.value)
                        ) {
                          handleUnselectOption(option);
                        } else {
                          handleSelectOption(option);
                        }
                      }}
                      className={cn("flex w-full items-center")}>
                      <span className="truncate pr-4">{option.label}</span>
                      {selectedOptions.some(
                        so => so.value === option.value,
                      ) && (
                        <div className="absolute top-0 right-2 bottom-0 flex w-4 items-center justify-center">
                          <CheckIcon className="size-4 shrink-0" />
                        </div>
                      )}
                    </RmCommandItem>
                  ))}
                </RmCommandGroup>
              ))}
            {!isLoading ? (
              <RmCommandEmpty>{emptyMessage}</RmCommandEmpty>
            ) : null}
          </RmCommandList>
        </RmPopoverContent>
      </CommandPrimitive>
    </RmPopover>
  );
};
